import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import {
  formatMoneyMinor,
  moneyDraftToMinor,
  sanitizeMoneyDraft,
} from "./money-input";

interface MoneyInputProps {
  valueMinor: number;
  disabled?: boolean;
  ariaLabel: string;
  onMinorChange: (valueMinor: number) => void;
}

export function MoneyInput({
  valueMinor,
  disabled = false,
  ariaLabel,
  onMinorChange,
}: MoneyInputProps) {
  const [draft, setDraft] = useState(() => formatMoneyMinor(valueMinor));
  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);
  const lastEmittedMinorRef = useRef(valueMinor);

  useEffect(() => {
    if (
      isFocusedRef.current &&
      lastEmittedMinorRef.current === valueMinor
    ) {
      return;
    }
    lastEmittedMinorRef.current = valueMinor;
    setDraft(formatMoneyMinor(valueMinor));
  }, [valueMinor]);

  const emitDraft = (nextDraft: string) => {
    const nextMinor = moneyDraftToMinor(nextDraft);
    lastEmittedMinorRef.current = nextMinor;
    if (nextMinor !== valueMinor) onMinorChange(nextMinor);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawDraft = event.currentTarget.value;
    const rawCaret = event.currentTarget.selectionStart ?? rawDraft.length;
    const nextDraft = sanitizeMoneyDraft(rawDraft);
    if (nextDraft !== rawDraft) {
      const nextCaret = sanitizeMoneyDraft(
        rawDraft.slice(0, rawCaret),
      ).length;
      queueMicrotask(() => {
        const input = inputRef.current;
        if (input && document.activeElement === input) {
          input.setSelectionRange(nextCaret, nextCaret);
        }
      });
    }
    setDraft(nextDraft);
    emitDraft(nextDraft);
  };

  const commitDraft = () => {
    if (valueMinor !== lastEmittedMinorRef.current) {
      const externalDraft = formatMoneyMinor(valueMinor);
      lastEmittedMinorRef.current = valueMinor;
      setDraft(externalDraft);
      return;
    }
    const nextMinor = moneyDraftToMinor(draft);
    const normalizedDraft = formatMoneyMinor(nextMinor);
    lastEmittedMinorRef.current = nextMinor;
    setDraft(normalizedDraft);
    if (normalizedDraft !== draft) {
      queueMicrotask(() => {
        const input = inputRef.current;
        if (input && document.activeElement === input) {
          input.setSelectionRange(normalizedDraft.length, normalizedDraft.length);
        }
      });
    }
    if (nextMinor !== valueMinor) onMinorChange(nextMinor);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    commitDraft();
  };

  return (
    <input
      ref={inputRef}
      className="money-input"
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={ariaLabel}
      value={draft}
      disabled={disabled}
      onFocus={(event) => {
        isFocusedRef.current = true;
        if (draft === "0.00") event.currentTarget.select();
      }}
      onClick={(event) => {
        if (draft === "0.00") event.currentTarget.select();
      }}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        isFocusedRef.current = false;
        commitDraft();
      }}
    />
  );
}
